import React, { FunctionComponent, createElement } from 'react';
import {
  Stepper as MuiStepper,
  Step as MuiStep,
  StepContent as MuiStepContent,
  StepLabel as MuiStepLabel,
  Typography as MuiTypography,
  makeStyles,
} from '@material-ui/core';
import { useTranslate, Translate } from 'react-admin';
import { HistoryItemsProps } from './types';
import { getIconComponentFromName } from '../../../helpers/icons';
import {
  getLocaleDateString,
  getLocaleTimeString,
} from '../../../helpers/date';
import {
  History as HistoryType,
  HistoryDetails,
} from '../../../config/types/history';
import { historyIcons } from '../../../constants/history';
import HistoryItemsChildsUpdated from './components/HistoryItemsChildsUpdated/HistoryItemsChildsUpdated';
import { isEmpty } from 'lodash';
import Note from '../Note';
import { ClassNameMap } from '@material-ui/styles';
import { getBooleanLabel } from '../../../helpers/history';
import { UPDATED_FIELD_BOOL } from '../../../constants/history';

const EmptyConnector = () => null;

const useStyles = makeStyles({
  root: {
    padding: '0px',
    '& .MuiStepConnector-root': {
      display: 'none',
    },
  },
  label: {
    color: 'black',
    fontWeight: 600,
    fontSize: '14px',
  },
  iconContainer: {
    color: 'grey',
  },
  content: {
    marginTop: '0px',
    color: 'grey',
    paddingBottom: '20px',
  },
  title: {
    color: 'black',
    fontSize: '14px',
  },
  subtitle: {
    color: 'black',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginLeft: '5px',
      width: '20px',
    },
  },
  details: {
    fontSize: '14px',
    whiteSpace: 'pre-wrap',
  },
  tag: {
    margin: '2px',
  },
  childs: {
    margin: '0px',
  },
  child: {
    margin: '2px',
    marginLeft: '-20px',
  },
  separator: {
    padding: '0px 2px',
  },
  date: {
    marginLeft: '5px',
  },
});

const TRANSLATION_KEY_ALIASES: Record<string, string> = {
  'history.invoice.field_updated.purchase_order':
    'history.contractmilestone.field_updated.purchase_order',
};

// Details whose `date` arg is shown with its time: the contract signatory's
// consent to automatic signature, given or withdrawn, kept as evidence
const DATE_WITH_TIME_TRANSLATION_KEYS = [
  'history.companies.contract_signature_consent_given',
  'history.companies.contract_signature_consent_revoked',
];

// For an unknown reason react-admin useTranslate hook modify the format of the date and display it in english
// this function allow to display the date outside of the useTranslate hook
const getDetailsTranslation = (
  details: HistoryDetails,
  t: Translate,
  classes: ClassNameMap<string>
): JSX.Element => {
  if (details.translation_key == undefined) {
    return <></>;
  }

  const translationKey =
    TRANSLATION_KEY_ALIASES[details.translation_key] ?? details.translation_key;

  // Raw before/after for a 'bool' field is `true`/`false`/`null` (never set
  // yet) -- interpolating that directly renders "true"/"null" instead of a
  // readable "Oui"/"Non"/"Aucun".
  const args =
    details.type === UPDATED_FIELD_BOOL && details.args
      ? {
          ...details.args,
          before: getBooleanLabel(details.args.before, t),
          after: getBooleanLabel(details.args.after, t),
        }
      : details.args;

  return (
    <>
      <span>{t(`${translationKey}.details`, args)}</span>
      {details.args &&
        typeof details.args.date !== 'undefined' &&
        details.args.date && (
          <span className={classes.date}>
            {getLocaleDateString(details.args.date as string)}
            {DATE_WITH_TIME_TRANSLATION_KEYS.includes(translationKey) &&
              ` ${getLocaleTimeString(details.args.date as string)}`}
          </span>
        )}
    </>
  );
};

const HistoryItems: FunctionComponent<HistoryItemsProps> = (props) => {
  const { history, resource, count } = props;
  const t = useTranslate();
  const classes = useStyles();
  const adminIcon = getIconComponentFromName('SupervisorAccount');

  // This function returns the date and the hour formatted
  const getHistoryDate = (date: string): JSX.Element => (
    <>
      <span>{getLocaleDateString(date)}</span>
      <span className={classes.date}>{getLocaleTimeString(date)}</span>
    </>
  );

  return (
    <MuiStepper
      orientation="vertical"
      classes={{ root: classes.root }}
      connector={<EmptyConnector />}
    >
      {history.map((h: HistoryType, index: number) => {
        const icon = getIconComponentFromName(historyIcons[h.key]);
        // `history` is fetched newest-first (see History/component.tsx's
        // sort=created_at,DESC) so it can be displayed and paginated that
        // way, but that leaves array position backwards from the actual
        // sequence of events. `count` (the *total* history size, stable
        // across "load more" pages) turns it back into a real step number:
        // the oldest event - the last one fetched - lands on 1, the newest
        // on `count`, regardless of how many pages are loaded so far.
        const stepNumber = count - index;

        return h.kind === 'note' ? (
          <MuiStep key={h.id} active>
            <Note history={h} updateHistory={props.updateHistory} />
          </MuiStep>
        ) : (
          <MuiStep key={h.id} active>
            <MuiStepLabel
              icon={stepNumber}
              StepIconComponent={icon}
              classes={{ iconContainer: classes.iconContainer }}
            >
              <MuiTypography className={classes.label}>
                {getHistoryDate(h.created_at)}
              </MuiTypography>
            </MuiStepLabel>
            <MuiStepContent classes={{ root: classes.content }}>
              <MuiTypography className={classes.title}>
                {t(`history.${resource.name}.${h.key}.title`)}
              </MuiTypography>
              {h.user_fullname && (
                <MuiTypography className={classes.subtitle}>
                  {h.user_fullname}
                  {h.admin_id && adminIcon && createElement(adminIcon)}
                </MuiTypography>
              )}
              {h.details && h.details.translation_key && (
                <MuiTypography className={classes.details}>
                  {getDetailsTranslation(h.details, t, classes)}
                </MuiTypography>
              )}
              {!isEmpty(h.childs) && (
                <HistoryItemsChildsUpdated childs={h.childs} />
              )}
            </MuiStepContent>
          </MuiStep>
        );
      })}
    </MuiStepper>
  );
};

export default HistoryItems;
